import { defineStore, storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { cloneDeep, isNil } from 'lodash-es';
import type { GlobalVarFilterDataForView } from '@/dashboard/features/globalVariables/components/globalVarFilter/types/globalVarFilter.type';
import { useDashboardViewStore } from './dashboard-view';
import { useStandardVariablesStore } from './global-variables/standard-variables';
import { useTreeVariablesStore } from './global-variables/tree-variables';
import { useVariablePresetsStore } from './global-variables/variable-presets';
import { useExternalVariablesStore } from './global-variables/external-variables';

/**
 * Global Filter Store (Facade)
 *
 * 여러 변수 타입(일반, 외부)과 프리셋을 통합 관리합니다.
 * 각 세부 store의 기능을 조합하여 제공합니다.
 *
 */
export const useGlobalFilterStore = defineStore('dashboardGlobalFilter', () => {
  // ============================================================
  // Sub Stores
  // ============================================================
  const standardVariablesStore = useStandardVariablesStore();
  const treeVariablesStore = useTreeVariablesStore();
  const variablePresetsStore = useVariablePresetsStore();
  const externalVariablesStore = useExternalVariablesStore();

  const { standardVariables } = storeToRefs(standardVariablesStore);
  const { externalVariables } = storeToRefs(externalVariablesStore);
  const { treeVariables } = storeToRefs(treeVariablesStore);
  const { presetList, currentPreset, defaultPreset } = storeToRefs(variablePresetsStore);

  // ============================================================
  // Dashboard ID
  // ============================================================
  const { monitoringDashboard } = storeToRefs(useDashboardViewStore());
  const monitoringDashboardId = computed(() => monitoringDashboard.value.dashboardId);

  // ============================================================
  // Fetch All Variables
  // ============================================================
  const fetchGlobalVariables = async () => {
    if (monitoringDashboardId.value === -1) return;

    await Promise.all([
      standardVariablesStore.fetchStandardVariables(),
      externalVariablesStore.fetchExternalVariables(),
      treeVariablesStore.fetchTreeVariables(),
    ]);

    // 프리셋 기본값 업데이트
    variablePresetsStore.updateDefaultPresetFilters(
      standardVariables.value,
      externalVariables.value,
      treeVariables.value,
    );
  };

  // ============================================================
  // Preset Management
  // ============================================================
  const fetchPresetList = async () => {
    await variablePresetsStore.fetchPresetList(
      monitoringDashboardId.value,
      standardVariables.value,
      externalVariables.value,
      treeVariables.value,
    );
  };

  const setCurrentPresetById = (presetId: number) => {
    const { filters, treeFilters } = variablePresetsStore.setCurrentPresetById(presetId);
    standardVariablesStore.replaceSelectedStandardVariables(filters);
    treeVariablesStore.applyPresetSelections(treeFilters);
  };

  const setDefaultPreset = () => {
    const { filters, treeFilters } = variablePresetsStore.setDefaultPreset();
    standardVariablesStore.replaceSelectedStandardVariables(filters);
    treeVariablesStore.applyPresetSelections(treeFilters);
  };

  const fetchCurrentPresetEnv = async () => {
    await variablePresetsStore.fetchCurrentPresetEnv();
  };

  const initCurrentPreset = () => {
    const { filters, treeFilters } = variablePresetsStore.initCurrentPreset(
      monitoringDashboardId.value,
    );
    standardVariablesStore.replaceSelectedStandardVariables(filters);
    treeVariablesStore.applyPresetSelections(treeFilters);
  };

  const saveCurrentPresetUserEnv = async (presetId: number) => {
    await variablePresetsStore.saveCurrentPresetUserEnv(monitoringDashboardId.value, presetId);
  };

  const savePreset = async (data: { name: string; filters: GlobalVarFilterDataForView[] }) => {
    const { selectedTreeVariables } = storeToRefs(treeVariablesStore);
    const savedPresetId = await variablePresetsStore.savePreset(monitoringDashboardId.value, {
      ...data,
      treeFilters: selectedTreeVariables.value,
    });
    await fetchGlobalVariables();
    await fetchPresetList();

    if (savedPresetId !== undefined) {
      setCurrentPresetById(savedPresetId);
      await saveCurrentPresetUserEnv(savedPresetId);
    }
  };

  const deletePreset = async (targetUserFilterIds: number[]) => {
    await variablePresetsStore.deletePreset(monitoringDashboardId.value, targetUserFilterIds);
    await fetchGlobalVariables();
    await fetchPresetList();
  };

  // ============================================================
  // Tree Variables → Preset 재적용
  // treeVariables는 async watch로 변환되므로, fetchPresetList/initCurrentPreset 시점에
  // 아직 비어있을 수 있다. treeVariables가 새로 할당되면 현재 프리셋의 treeFilters를 재적용한다.
  // ============================================================
  watch(treeVariables, (newVars) => {
    if (newVars.length > 0) {
      // 기본 프리셋 treeFilters 업데이트
      variablePresetsStore.updateDefaultPresetFilters(
        standardVariables.value,
        externalVariables.value,
        newVars,
      );
      // 현재 프리셋의 treeFilters 적용
      const treeFilters = cloneDeep(currentPreset.value.treeFilters);
      treeVariablesStore.applyPresetSelections(treeFilters);
    }
  });

  // ============================================================
  // Computed - All Variables Combined
  // ============================================================
  const allVariablesCount = computed(() => {
    return (
      standardVariables.value.length + externalVariables.value.length + treeVariables.value.length
    );
  });

  const hasAnyVariables = computed(() => allVariablesCount.value > 0);

  const globalFilterIdList = computed<Set<number>>(() => {
    const ids = new Set<number>();
    for (const item of standardVariables.value) {
      if (!isNil(item.globalFilterId)) ids.add(item.globalFilterId);
    }
    for (const item of externalVariables.value) {
      if (!isNil(item.extraGlobalFilterId)) ids.add(item.extraGlobalFilterId);
    }
    return ids;
  });

  const treeFilterIdList = computed<Set<number>>(() => {
    const ids = new Set<number>();
    for (const item of treeVariables.value) {
      if (!isNil(item.id)) ids.add(item.id);
    }
    return ids;
  });

  return {
    // Fetch All Variables
    fetchGlobalVariables,

    // Presets
    presetList,
    currentPreset,
    defaultPreset,
    fetchPresetList,
    setCurrentPresetById,
    setDefaultPreset,
    fetchCurrentPresetEnv,
    initCurrentPreset,
    saveCurrentPresetUserEnv,
    savePreset,
    deletePreset,

    // Computed
    allVariablesCount,
    hasAnyVariables,
    globalFilterIdList,
    treeFilterIdList,
  };
});
